import lodash from 'lodash';
import omitDeep from 'omit-deep';

import type { DraftContent } from '..';
import type { RawDraftEntityRange } from '../types/draft-js';
import { fromEntries } from '../utils';

const IGNORED_KEYS = [
  'updatedTimestamp',
  'createdTimestamp',
  'tempData',
  'isCustomVideo',
  'VERSION',
];
const IGNORED_POLL_CONFIG_KEYS = ['alignment', 'size', 'width'];
const IGNORED_SRC_KEYS = ['id', 'original_file_name'];
const IGNORED_BUTTON_DESIGN_KEYS = ['activeButton', 'padding'];
const OEMBED_KEYS = ['thumbnail_url', 'width', 'height'];

/**
 * Deep diff between two objects.
 * @param object Object compared
 * @param base   Object to compare to
 * @return       Return a new object that represents the diff
 */
export function compare(
  object: any,
  base: any,
  options: { verbose?: boolean; ignoredKeys?: string[] } = {}
) {
  const { verbose, ignoredKeys } = options;
  const allIgnoredKeys = [...IGNORED_KEYS, ...(ignoredKeys || [])];

  const objectWithoutIgnored: $TSFixMe = omitDeep(lodash.cloneDeep(object), allIgnoredKeys);
  const basetWithoutIgnored: $TSFixMe = omitDeep(lodash.cloneDeep(base), allIgnoredKeys);

  objectWithoutIgnored.blocks && removeEmoji(objectWithoutIgnored);
  basetWithoutIgnored.blocks && removeEmoji(basetWithoutIgnored);

  const ARE_EQUAL = {};

  const getChanges = (value: $TSFixMe, base: $TSFixMe): $TSFixMe => {
    if (value === base) {
      return ARE_EQUAL;
    } else if (lodash.isEqualWith(value, base, comparator as any)) {
      return ARE_EQUAL;
    } else if (lodash.isArray(value) && lodash.isArray(base)) {
      const items = value
        .map((valueItem, i) => getChanges(valueItem, base[i]))
        .filter(item => item !== ARE_EQUAL);
      if (items.length) {
        return items;
      } else {
        return ARE_EQUAL;
      }
    } else if (lodash.isObject(value) && lodash.isObject(base)) {
      const keys = Array.from(new Set<string>([...Object.keys(value), ...Object.keys(base)]));
      const entries = keys
        .map(key => [
          key,
          getChanges(
            value[key as $TSFixMeKey<typeof value>],
            base[key as $TSFixMeKey<typeof base>]
          ),
        ])
        .filter(([_, result]) => result !== ARE_EQUAL);
      if (entries.length) {
        return Object.fromEntries(entries);
      } else {
        return ARE_EQUAL;
      }
    } else {
      return value;
    }
  };

  const result = getChanges(objectWithoutIgnored, basetWithoutIgnored);

  if (verbose && result !== ARE_EQUAL) {
    console.dir(result); // eslint-disable-line no-console
  }

  return result;
}

const comparator = (
  left: Record<string, any> | undefined, // defined for type safety, could have any value
  right: Record<string, any> | undefined,
  key: string
) => {
  if (left?.enableVoteRole !== undefined || right?.enableVoteRole !== undefined) {
    return lodash.isEqual(
      lodash.omit(left, IGNORED_POLL_CONFIG_KEYS),
      lodash.omit(right, IGNORED_POLL_CONFIG_KEYS)
    );
  }
  if (left?.thumbnail_url || right?.thumbnail_url) {
    return lodash.isEqual(lodash.pick(left, OEMBED_KEYS), lodash.pick(right, OEMBED_KEYS));
  }
  if (key === 'src') {
    return lodash.isEqual(
      lodash.omit(left, IGNORED_SRC_KEYS),
      lodash.omit(right, IGNORED_SRC_KEYS)
    );
  }
  if (key === 'design') {
    return lodash.isEqual(
      lodash.omit(left, IGNORED_BUTTON_DESIGN_KEYS),
      lodash.omit(right, IGNORED_BUTTON_DESIGN_KEYS)
    );
  }
  if (key === 'link' && (right?.rel === 'noopener' || left?.rel === 'noopener')) {
    return lodash.isEqual(lodash.omit(left, ['rel']), lodash.omit(right, ['rel']));
  }
  return undefined;
};

const removeEmoji = (object: DraftContent) => {
  const emojiEntityKeys: number[] = [];
  Object.entries<any>(object.entityMap).forEach(
    ([key, value]) => value.type === 'EMOJI_TYPE' && emojiEntityKeys.push(parseInt(key, 10))
  );
  object.entityMap = fromEntries(
    Object.entries(object.entityMap).filter(([, value]) => value.type !== 'EMOJI_TYPE')
  );
  object.blocks = object.blocks.map(block => ({
    ...block,
    entityRanges: (block.entityRanges as RawDraftEntityRange[]).filter(
      (range: RawDraftEntityRange) => !emojiEntityKeys.includes(range.key)
    ),
  }));
};
